import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Analytics } from '@vercel/analytics/react';

import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { UserContextProvider } from './contexts/UserContext';
import { SuperAdminAuthProvider } from './contexts/SuperAdminAuthContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { BranchProvider } from './contexts/BranchContext';
import { MemberAuthProvider } from './contexts/MemberAuthContext';
import { installImpersonationFetch } from './utils/installImpersonationFetch';
import './index.css';
import { registerSW } from 'virtual:pwa-register';

// Super-admin "browse as gym": make raw-fetch CRM pages carry the impersonation
// header. Installed before render so the very first API call is covered.
installImpersonationFetch();

// Register Service Worker for PWA
const updateSW = registerSW({
  onNeedRefresh() {
    if (confirm('New version available. Reload now?')) {
      updateSW(true);
    }
  },
  onOfflineReady() {
    console.log('App ready to work offline');
  },
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false, // Prevent refresh on tab switch
    },
  },
});

// Get reCAPTCHA site key from environment
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ImpersonationProvider>
          <UserContextProvider>
            <BranchProvider>
            <SuperAdminAuthProvider>
              <GoogleReCaptchaProvider
                reCaptchaKey={RECAPTCHA_SITE_KEY}
                scriptProps={{
                  async: true,
                  defer: true,
                  appendTo: 'head',
                }}
                container={{
                  parameters: {
                    badge: 'bottomright',
                  },
                }}
              >
                <MemberAuthProvider>
                  <App />
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#363636',
                        color: '#fff',
                      },
                    }}
                  />
                  <Analytics />
                </MemberAuthProvider>
              </GoogleReCaptchaProvider>
            </SuperAdminAuthProvider>
            </BranchProvider>
          </UserContextProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
); 